import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, type NavigateOptions, type To } from 'react-router-dom';
import { logger } from '@utils/logger';
import { StableRouterContext, type StableRouterValue } from './stableRouterContext';

interface StableRouterProviderProps {
  children: React.ReactNode;
}

/**
 * Would react-router resolve this `to` against the CALLING component's route? Those are the only
 * values this provider cannot forward faithfully, because the `useNavigate()` it wraps is bound
 * where the provider is mounted, not where the caller is.
 *
 * Mirrors `resolveTo` in @remix-run/router: a `to` whose pathname part is absent - a bare `?search`
 * or `#hash` - resolves against the current LOCATION and so is unaffected by where the hook was
 * called; an empty pathname collapses to `/`; anything else not starting with `/` is route-relative.
 */
function isRouteRelative(to: To): boolean {
  if (typeof to !== 'string') {
    const { pathname } = to;
    if (pathname === undefined) return false;
    return pathname === '' || !pathname.startsWith('/');
  }
  // `navigate('')` means "re-navigate to the current route", which resolveTo turns into `/`.
  if (to === '') return true;
  const pathname = to.split('#')[0].split('?')[0];
  if (pathname === '') return false;
  return !pathname.startsWith('/');
}

/**
 * Holds the app's ONE router subscription on behalf of every descendant that only needs to navigate
 * or to read the current URL. See `stableRouterContext.ts` for why that is worth a provider.
 *
 * This component re-renders on every navigation - that is the point, it is where the subscription
 * is paid, once, on a component that renders nothing of its own. Its `children` prop is the same
 * element object across those re-renders (the parent that composed it did not re-render), so React
 * bails out of the subtree immediately, and the context value below never changes identity, so no
 * consumer is woken either.
 *
 * Mounted at `RootLayout`, above `<Outlet />`, so it covers every route.
 */
export const StableRouterProvider: React.FC<StableRouterProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  // MUST be a LAYOUT effect, not `useLatestRef`'s passive one. This provider is an ancestor of every
  // consumer, and React flushes effects child-first within a phase, so a passive sync here would be
  // the LAST effect of the commit: a card mounted by a navigation would run its own mount effect
  // first and read the location the user just left. Layout effects all run before any passive
  // effect, so a descendant's `useEffect` sees the current URL. Synchronous-in-commit also closes
  // the tap-handler window: React 19's `dispatchDiscreteEvent` does NOT flush pending passive
  // effects (that was React 17's `flushDiscreteUpdatesIfNeeded`), so with a passive sync a tap
  // landing in the same task as a navigation could still read a stale ref.
  // Still child-first WITHIN the layout phase, so a descendant's own `useLayoutEffect` remains one
  // navigation behind - `readLocation()` is not safe there. Writing during render would be simplest
  // but breaks the Rules of React and bails the React Compiler out.
  useLayoutEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [navigate, location]);

  // Refs are stable for this component's lifetime, so this object is built exactly once. Load-
  // bearing rather than an optimisation: a context value that changed identity would re-render every
  // consumer on every navigation, which is the precise cost this provider exists to remove.
  const value = useMemo<StableRouterValue>(
    () => ({
      // GUARANTEED to match `useNavigate()`: absolute paths, `Partial<Path>` objects, bare
      // `?search` / `#hash` strings (react-router resolves those against the current location, not
      // against a route), numeric deltas, and arity - the rest tuple forwards `navigate(path)` as
      // ONE argument rather than `(path, undefined)`, so `toHaveBeenCalledWith(path)` assertions
      // still hold and nothing downstream reads an argument the caller never passed.
      //
      // NOT guaranteed, so this is a drop-in for most callers rather than all of them:
      //   1. Route-relative `to` values resolve from the WRONG place. `useNavigate()` resolves them
      //      against the route of the component that called it; this hook was called in the
      //      provider, so `navigate('edit')` from a card resolves against RootLayout's route (`/`),
      //      not the card's. Under the data router the same substitution happens through
      //      `fromRouteId`, which `useNavigateStable` fills in from its own route. Nothing in `src`
      //      passes a relative string today - every target comes from `@routes` or `slugHelpers`,
      //      and the only non-absolute call is `navigate(-1)` - so the dev warning below is here to
      //      keep it that way rather than to report a live bug.
      //   2. The return value is dropped. Faithful in react-router 6, where `NavigateFunction`
      //      returns void; on a react-router 7 upgrade it returns `void | Promise<void>` and an
      //      `await navigate(...)` through here would resolve immediately instead of on completion.
      navigate: (to: To | number, ...rest: [NavigateOptions?]) => {
        // The numeric-delta overload takes no options; react-router ignores them, and splitting
        // here says so rather than leaving it implied.
        if (typeof to === 'number') {
          navigateRef.current(to);
          return;
        }
        if (import.meta.env.DEV && isRouteRelative(to)) {
          logger.warn(
            `[StableRouterProvider] navigate(${JSON.stringify(to)}) is route-relative, and this ` +
              `seam resolves it from RootLayout's route rather than the caller's. Pass an absolute ` +
              `path (see @routes / slugHelpers), or use react-router's useNavigate() here and ` +
              `accept the re-render it costs.`,
          );
        }
        navigateRef.current(to, ...rest);
      },
      readLocation: () => locationRef.current,
    }),
    [],
  );

  return <StableRouterContext.Provider value={value}>{children}</StableRouterContext.Provider>;
};
